import React from 'react';


const BusinessDevResumePreview = ({ componentRef, resumeData = {} }) => {
  // Process highlighting markers from AI response
  
  const {
    full_name,  
    email,
    phone,
    location,
    websites,
    projects,
    professional_summary,
    relevant_experience,
    education,
    certifications,
    achievements,
    skills = {},
    key_skills = [],
    tools_tech = [],
    training = [],
    languages = [],
    reference
  } = resumeData;

  const {
    programming_languages = [],
    frameworks = [],
    tools = [],
    ai_ml_technologies = []
  } = skills || {};

  const isUploadedResume = resumeData && Object.keys(resumeData).length > 0 && !resumeData.isTemplate;
  const isTemplateResume = resumeData && resumeData.isTemplate;
  
  // Enhanced logic to detect when to show user data
  const shouldShowUserData = resumeData && (
      !resumeData.isTemplate || 
      resumeData.full_name || 
      resumeData.job_title ||
      resumeData.email || 
      resumeData.phone || 
      resumeData.location ||
      resumeData.websites ||
      resumeData.professional_summary ||
      resumeData.projects ||
      resumeData.education ||
      resumeData.relevant_experience ||
      resumeData.skills ||
      resumeData.key_skills ||
      resumeData.achievements ||
      resumeData.certifications
  );

  // IMPROVED CONTENT FLOW LOGIC - Better page break decisions
  const calculatePageBreakStrategy = () => {
    let estimatedHeight = 0;
    const A4_HEIGHT_ESTIMATE = 950; // Approximate pixels for A4 in print
    const HEADER_HEIGHT = 100;
    const SECTION_HEADER_HEIGHT = 30;
    const CONTENT_LINE_HEIGHT = 20;
    
    // Header
    estimatedHeight += HEADER_HEIGHT;
    
    // Professional Summary
    if (professional_summary) {
      estimatedHeight += SECTION_HEADER_HEIGHT + CONTENT_LINE_HEIGHT * 2;
    }
    
    // Skills
    if (key_skills?.length > 0) {
      estimatedHeight += SECTION_HEADER_HEIGHT + CONTENT_LINE_HEIGHT * 2;
    }
    
    // Experience
    if (relevant_experience?.length) {
      estimatedHeight += SECTION_HEADER_HEIGHT;
      relevant_experience.forEach(exp => {
        estimatedHeight += CONTENT_LINE_HEIGHT * 2; // Header
        estimatedHeight += CONTENT_LINE_HEIGHT * (exp.description_points?.length || 2);
      });
    }
    
    // Education height estimate
    let educationHeight = 0;
    if (education?.length) {
      educationHeight += SECTION_HEADER_HEIGHT;
      education.forEach(edu => {
        educationHeight += CONTENT_LINE_HEIGHT * 3; // Title, location, details
      });
    }
    
    // Achievements height
    let achievementsHeight = 0;
    if (achievements?.length) {
      achievementsHeight += SECTION_HEADER_HEIGHT + (achievements.length * CONTENT_LINE_HEIGHT);
    }
    
    // Projects height
    let projectsHeight = 0;
    if (projects?.length) {
      projectsHeight += SECTION_HEADER_HEIGHT;
      projects.forEach(proj => {
        projectsHeight += CONTENT_LINE_HEIGHT * 3; // Title, description, tech
      });
    }
    
    // Certifications height
    let certificationsHeight = 0;
    if (certifications?.length) {
      certificationsHeight += SECTION_HEADER_HEIGHT + (certifications.length * CONTENT_LINE_HEIGHT);
    }
    
    // Calculate total height
    const totalHeight = estimatedHeight + educationHeight + achievementsHeight + projectsHeight + certificationsHeight;
    
    return {
      shouldSplit: totalHeight > A4_HEIGHT_ESTIMATE,
      totalHeight,
      educationHeight,
      achievementsHeight,
      projectsHeight,
      certificationsHeight
    };
  };

  const pageBreakStrategy = calculatePageBreakStrategy();

  const shouldProjectsOverflow = () => {
    if (!projects || !Array.isArray(projects)) return false;
    
    // For uploaded resumes, check if we have substantial content that needs splitting
    const totalSections =
      (relevant_experience?.length || 0) +
      (education?.length || 0) +
      (certifications?.length || 0) +
      (achievements?.length || 0);
    
    // If we have projects and substantial other content, split into multiple pages
    return projects.length > 0 && (totalSections > 2 || projects.length > 1);
  };

  // Handle both template and uploaded resume data
  const displaySkills = key_skills.length > 0 || 
    (programming_languages.length > 0 || frameworks.length > 0 || tools.length > 0 || ai_ml_technologies.length > 0);



  return (
    <div id="section-to-print" ref={componentRef} className="cvguy-template-wrapper">
      {/* PAGE 1 */}
      <div className="cvguy-template-page">
        {/* LEFT COLUMN */}
        <div className="cvguy-left-column">
          {/* HEADER SECTION */}
          <div className="cvguy-header">
            <div className="cvguy-profile-section">
              {/* Profile Photo */}
              <div className="cvguy-profile-photo">
                <img 
                  src="./english.jpeg"
                  alt="Profile" 
                  className="cvguy-profile-photo-img"
                />
              </div>
              {/* Name and Title */}
              <div className="cvguy-header-content">
                <h1 className="cvguy-name">
                  {shouldShowUserData && full_name ? full_name : "TheCVGuy.net"}
                </h1>
                <p className="cvguy-subtitle">
                  Business Development Professional
                </p>
              </div>
            </div>
          </div>

          {/* Professional Summary */}
          <div className="cvguy-summary-section">
            <p className="cvguy-summary-text" style={{ fontSize: '11px', lineHeight: '1.4', color: '#000', textAlign: 'justify', margin: 0 }}>
              {shouldShowUserData && professional_summary ? professional_summary : "Results-driven Manager over [insert years of experience] years of proven success in Business Development. Adept at driving revenue growth, forging strategic partnerships, and leading high-performing teams. Demonstrated ability to diversify product portfolios and achieve remarkable revenue milestones, including a 50% annual growth rate. Experienced in market analysis, budget management, and cross-functional collaboration. Known for establishing and nurturing key client and partner relationships."}
            </p>
          </div>

          {/* WORK EXPERIENCE */}
          {relevant_experience && relevant_experience.length > 0 && (
            <div className="cvguy-section">
              <h2 className="cvguy-section-title">
                <span className="section-icon">💼</span>
                WORK EXPERIENCE
              </h2>
              {relevant_experience.map((exp, i) => (
                <div key={i} className="cvguy-experience-item">
                  <div className="experience-header">
                    <div className="experience-title-section">
                      <h3 className="experience-position">{exp.position}</h3>
                      <p className="experience-company">{exp.company}</p>
                    </div>
                    <div className="experience-dates">
                      {exp.start_date} - {exp.end_date}
              </div>
                  </div>
                  <div className="experience-content">
                    {exp.achievements && exp.achievements.length > 0 && (
                      <div className="experience-subsection">
                        <h4 className="subsection-title">Achievements:</h4>
                        <ul className="experience-list">
                          {exp.achievements.map((point, j) => (
                            <li key={j}>{point}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {exp.description_points && exp.description_points.length > 0 && (
                      <div className="experience-subsection">
                        <h4 className="subsection-title">Duties & Responsibilities:</h4>
                        <ul className="experience-list">
                          {exp.description_points.map((point, j) => (
                            <li key={j}>{point}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
              </div>
              ))}
            </div>
          )}

          {/* EDUCATIONAL QUALIFICATIONS */}
          {education && education.length > 0 && (
            <div className="cvguy-section">
              <h2 className="cvguy-section-title">
                <span className="section-icon">🎓</span>
                EDUCATIONAL QUALIFICATIONS
              </h2>
              {education.map((edu, i) => (
                <div key={i} className="cvguy-education-item">
                  <h3 className="education-degree">
                    {edu.degree} - {edu.end_year}
                  </h3>
                  <p className="education-institution">{edu.institution}</p>
                  {edu.cgpa && (
                    <ul className="education-details">
                      <li>CGPA: {edu.cgpa}</li>
                    </ul>
                  )}
            </div>
              ))}
            </div>
          )}



          {/* ACHIEVEMENTS - after projects */}
          {achievements && achievements.length > 0 && (
            <div className="cvguy-section">
              <h2 className="cvguy-section-title">
                <span className="section-icon">🏆</span>
                ACHIEVEMENTS
              </h2>
              <ul className="cvguy-achievements-list">
                {achievements.map((achievement, i) => (
                  <li key={i}>{achievement}</li>
                ))}
              </ul>
            </div>
          )}

          {/* CERTIFICATIONS */}
          {certifications && certifications.length > 0 && (
            <div className="cvguy-section">
              <h2 className="cvguy-section-title">
                <span className="section-icon">🏅</span>
                CERTIFICATIONS
              </h2>
              <ul className="cvguy-certifications-list">
                {certifications.map((cert, i) => {
                  const name = cert.name || cert.title || cert;
                  const provider = cert.issuer || cert.organization || cert.provider;
                  
                  return (
                    <li key={i}>
                      {name}
                      {provider && ` – ${provider}`}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN (SIDEBAR) */}
        <div className="cvguy-right-column">
          {/* CONTACT INFO (Sidebar) */}
          <div className="cvguy-sidebar-section">
            <div className="cvguy-contact-info-sidebar">
              <div className="contact-item">
                <span className="contact-icon">✉</span>
                <span style={{ fontSize: '12px', color: '#000', fontWeight: '700', marginBottom: '4px', textDecoration: 'underline' }}>
                  {shouldShowUserData && email ? email : "thecvguy@gmail.com"}
                </span>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📞</span>
                <span style={{ fontSize: '12px', color: '#000', fontWeight: '700', marginBottom: '4px' }}>
                  {shouldShowUserData && phone ? phone : "+88 01817110000"}
                </span>
            </div>
              <div className="contact-item">
                <span className="contact-icon">🔗</span>
                <span style={{ fontSize: '12px', color: '#000', fontWeight: '700', marginBottom: '4px', textDecoration: 'underline' }}>
                  {shouldShowUserData && websites?.[0] ? websites[0] : "in/thecvguy.net/"}
                </span>
                </div>
              <div className="contact-item">
                <span className="contact-icon">📍</span>
                <span style={{ fontSize: '12px', color: '#000', fontWeight: '700', marginBottom: '4px' }}>
                  {shouldShowUserData && location ? location : "Dhaka, Bangladesh"}
                </span>
              </div>
            </div>
                      </div>

          {/* WEBSITES/LINKS */}
          {shouldShowUserData && websites && websites.length > 1 && (
            <div className="cvguy-sidebar-section">
              <h3 className="cvguy-sidebar-title">
                <span className="sidebar-icon">🌐</span>
                WEBSITES/LINKS
              </h3>
              <div className="websites-list">
                {websites.slice(1).map((site, index) => (
                  <div key={index} className="website-item">
                    <a href={site} target="_blank" rel="noopener noreferrer" style={{ 
                      fontSize: '11px', 
                      color: '#000', 
                      textDecoration: 'underline',
                      wordBreak: 'break-all'
                    }}>
                      {site.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CORE COMPETENCIES */}
          {displaySkills && (
            <div className="cvguy-sidebar-section">
              <h3 className="cvguy-sidebar-title">
                <span className="sidebar-icon">⚙️</span>
                CORE COMPETENCIES
              </h3>
              {key_skills.length > 0 ? (
                <div className="competencies-grid">
                  {key_skills.map((skill, index) => (
                    <div key={index} className="competency-tag">
                      {skill}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="competencies-grid">
                  {[
                    { label: "Programming Languages", items: programming_languages },
                    { label: "Frameworks", items: frameworks },
                    { label: "Tools", items: tools },
                    { label: "AI/ML Technologies", items: ai_ml_technologies }
                  ]
                    .filter(({ items }) => items.length > 0)
                    .map(({ label, items }) => (
                      <div key={label} className="competency-tag">
                        <strong>{label}:</strong> {items.join(', ')}
                  </div>
                ))}
              </div>
              )}
            </div>
          )}

          {/* TOOLS & TECH */}
          <div className="cvguy-sidebar-section">
            <h3 className="cvguy-sidebar-title">
              <span className="sidebar-icon">🔧</span>
              TOOLS & TECH
            </h3>
            <div className="tools-grid">
              {(tools_tech || []).map((tool, index) => (
                <div key={index} className="tool-tag">
                  {tool}
                </div>
              ))}
            </div>
              </div>

          {/* TRAINING */}
          <div className="cvguy-sidebar-section">
            <h3 className="cvguy-sidebar-title">
              <span className="sidebar-icon">📚</span>
              TRAINING
            </h3>
            <div className="training-content">
              {(training || []).map((item, i) => (
                <div key={i} className="training-item">
                  <h4 className="training-title">
                    {item.title}- {item.year}
                  </h4>
                  <p className="training-institute">{item.institute}</p>
                  <p className="training-description">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>

          {/* LANGUAGES */}
          <div className="cvguy-sidebar-section">
            <h3 className="cvguy-sidebar-title">
              <span className="sidebar-icon">🌍</span>
              LANGUAGES
            </h3>
            <div className="languages-content">
              {(languages || []).map((lang, i) => (
                <div key={i} className="language-item">
                  <div className="language-name">{lang.name}</div>
                  <div className="language-bar">
                    <div className="language-progress" style={{ width: `${lang.proficiency}%` }}></div>
                  </div>
                  </div>
                ))}
              </div>
          </div>

          {/* REFERENCE */}
          <div className="cvguy-sidebar-section">
            <h3 className="cvguy-sidebar-title">
              REFERENCE
            </h3>
            <div className="reference-content">
              {reference && (
                <div className="reference-item">
                  <div className="reference-name">{reference.name}</div>
                  <div className="reference-designation">{reference.designation}</div>
                  <div className="reference-company">{reference.company}</div>
                  <div className="reference-contact">{reference.email}</div>
                  <div className="reference-contact">{reference.phone}</div>
            </div>
          )}
            </div>
          </div>
        </div>
      </div>


      {/* PAGE 2 — only if needed */}
      {shouldProjectsOverflow() && (
        <div className="cvguy-template-page">
          <div className="cvguy-left-column">
            <div className="cvguy-section">
              <h2 className="cvguy-section-title">
                <span className="section-icon">💡</span>
                PROJECTS
              </h2>
              {projects.map((proj, i) => (
                <div key={i} className="project-item">
                  <h3>
                    <strong>{proj.title}</strong>{proj.year ? ` (${proj.year})` : ''}
                  </h3>
                  <p>
                    <em>Tech Stack:</em> {Array.isArray(proj.tech_stack) ? proj.tech_stack.map((item, idx) => (
                      <span key={idx}>{idx > 0 ? ', ' : ''}{item}</span>
                    )) : proj.tech_stack}
                  </p>
                  <ul className="project-list">
                    {Array.isArray(proj.description) ? 
                      proj.description.map((d, j) => (
                        <li key={j}>{d}</li>
                      )) : 
                      <li>{proj.description}</li>
                    }
                  </ul>
                  {proj.github_link && (
                    <a href={proj.github_link} target="_blank" rel="noreferrer" className="project-link">
                      GitHub
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="cvguy-right-column"></div>
        </div>
      )}
    </div>
  );
};

export default BusinessDevResumePreview;